import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
  IconButton,
  Link,
  Slide,
} from '@mui/material';
import {
  Close as CloseIcon,
  Settings as SettingsIcon,
  PrivacyTip as PrivacyIcon,
} from '@mui/icons-material';
import type { TransitionProps } from '@mui/material/transitions';
import { useConsent } from '../../hooks/useConsent';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface ConsentBannerProps {
  position?: 'bottom' | 'top';
  variant?: 'banner' | 'modal';
}

const ConsentBanner: React.FC<ConsentBannerProps> = ({
  position = 'bottom',
  variant = 'banner',
}) => {
  const {
    consentState,
    updateConsent,
    resetConsent,
    isConsentRequired,
    shouldShowBanner,
  } = useConsent();

  const [showDetails, setShowDetails] = useState(false);
  const [localConsent, setLocalConsent] = useState(consentState);

  useEffect(() => {
    setLocalConsent(consentState);
  }, [consentState]);

  const handleAcceptAll = () => {
    console.log('🟢 User clicked Accept All');
    const consentToSave = {
      analytics: true,
      marketing: true,
      functional: true,
      necessary: true,
    };
    console.log('📝 Saving consent:', consentToSave);
    updateConsent(consentToSave);
  };

  const handleRejectAll = () => {
    console.log('🔴 User clicked Reject All');
    const consentToSave = {
      analytics: false,
      marketing: false,
      functional: false,
      necessary: true, // Always required
    };
    console.log('📝 Saving consent:', consentToSave);
    updateConsent(consentToSave);
  };

  const handleCustomize = () => {
    setShowDetails(true);
  };

  const handleSaveCustom = () => {
    console.log('⚙️ User clicked Save Custom Preferences');
    console.log('📝 Saving custom consent:', localConsent);
    updateConsent(localConsent);
    setShowDetails(false);
  };

  const handleConsentChange =
    (category: keyof typeof localConsent) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setLocalConsent(prev => ({
        ...prev,
        [category]: event.target.checked,
      }));
    };

  // Debug logging
  console.log('🎭 ConsentBanner render state:', {
    isConsentRequired: isConsentRequired(),
    shouldShowBanner: shouldShowBanner(),
    consentState,
    hasConsent: consentState,
  });

  if (!isConsentRequired() || !shouldShowBanner()) {
    console.log('🚫 ConsentBanner hidden - not required or already consented');
    return null;
  }

  console.log('✅ ConsentBanner will render');

  const BannerContent = () => (
    <Alert
      severity="info"
      variant="filled"
      sx={{
        position: 'fixed',
        [position]: 0,
        left: 0,
        right: 0,
        zIndex: 1300,
        borderRadius: 0,
        '& .MuiAlert-message': {
          width: '100%',
        },
      }}
      action={
        <IconButton
          color="inherit"
          size="small"
          onClick={handleRejectAll}
          aria-label="Close banner"
        >
          <CloseIcon />
        </IconButton>
      }
      icon={<PrivacyIcon />}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" spacing={2}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            We value your privacy
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            We use cookies and similar technologies to enhance your browsing experience,
            analyze site traffic, and improve our services. By clicking "Accept All", you
            consent to our use of cookies.{' '}
            <Link
              href="/privacy"
              color="inherit"
              underline="always"
              sx={{ fontWeight: 500 }}
            >
              Learn more
            </Link>
          </Typography>
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ minWidth: 'fit-content' }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={handleCustomize}
            startIcon={<SettingsIcon />}
            sx={{
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: 'inherit',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Customize
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleRejectAll}
            sx={{
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: 'inherit',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Reject All
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleAcceptAll}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'inherit',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            Accept All
          </Button>
        </Stack>
      </Stack>
    </Alert>
  );

  const ConsentDetailsDialog = () => (
    <Dialog
      open={showDetails}
      onClose={() => setShowDetails(false)}
      TransitionComponent={Transition}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PrivacyIcon color="primary" />
          <Typography variant="h6">Cookie Preferences</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          <Typography variant="body2" color="text.secondary">
            Manage your cookie preferences. You can enable or disable different types of
            cookies below. Note that disabling some types of cookies may impact your
            experience on our site.
          </Typography>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={localConsent.necessary}
                  disabled={true}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle2">Necessary Cookies</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Required for the website to function properly. Cannot be disabled.
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={localConsent.functional}
                  onChange={handleConsentChange('functional')}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle2">Functional Cookies</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Enable enhanced functionality like chat widgets and user preferences.
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={localConsent.analytics}
                  onChange={handleConsentChange('analytics')}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle2">Analytics Cookies</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Help us understand how visitors interact with our website by
                    collecting anonymous information.
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={localConsent.marketing}
                  onChange={handleConsentChange('marketing')}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle2">Marketing Cookies</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Used to deliver more relevant advertisements and track the
                    effectiveness of marketing campaigns.
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            For more information about how we use cookies, please read our{' '}
            <Link href="/privacy" color="primary">
              Privacy Policy
            </Link>{' '}
            and{' '}
            <Link href="/cookies" color="primary">
              Cookie Policy
            </Link>
            .
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={() => setShowDetails(false)} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSaveCustom} variant="contained">
          Save Preferences
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      {variant === 'banner' && <BannerContent />}
      <ConsentDetailsDialog />
    </>
  );
};

export default ConsentBanner;
